import { useEffect, useState } from "react";

/**
 * Full-screen splash shown on app boot.
 * - Same identity as BrandPanel (dark navy + gold "معيار")
 * - Fully responsive: the name scales via clamp(), ornaments/frame stay
 *   readable on the smallest phone up to 4K monitors.
 * - No external images: pure CSS + SVG so it renders instantly.
 */
export default function SplashScreen({ onDone, minDurationMs = 1400 }) {
    const [hide, setHide] = useState(false);
    // Read persisted user-chosen language without depending on LangProvider —
    // splash mounts before any context is available.
    const lang = (typeof window !== "undefined" && window.localStorage?.getItem("app_lang")) || "ar";
    const brand = lang === "en" ? "Meyar" : "معيار";
    const tag = lang === "en" ? "Al Meyar • Gold Accounting System" : "Al Meyar • نظام محاسبة الذهب";

    useEffect(() => {
        const t1 = setTimeout(() => setHide(true), minDurationMs);
        const t2 = setTimeout(() => onDone?.(), minDurationMs + 500);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [minDurationMs, onDone]);

    return (
        <div
            className={`fixed inset-0 z-[9998] overflow-hidden bg-[#0b1e33] transition-opacity duration-500 ${
                hide ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
            dir={lang === "en" ? "ltr" : "rtl"}
            data-testid="splash-screen"
        >
            {/* Warm radial glow */}
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 45%, rgba(180,142,75,0.28) 0%, rgba(180,142,75,0.1) 35%, rgba(11,30,51,0) 65%)",
                }}
            />

            {/* Dotted arabesque pattern (corners) */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.22]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(217,175,102,0.85) 1px, transparent 1.5px)",
                    backgroundSize: "14px 14px",
                    maskImage:
                        "radial-gradient(circle at 90% 6%, black 0%, black 14%, transparent 36%), radial-gradient(circle at 10% 94%, black 0%, black 12%, transparent 34%)",
                    WebkitMaskImage:
                        "radial-gradient(circle at 90% 6%, black 0%, black 14%, transparent 36%), radial-gradient(circle at 10% 94%, black 0%, black 12%, transparent 34%)",
                }}
            />

            {/* Octagonal gold frame — hidden on very small screens, subtle otherwise */}
            <svg
                className="pointer-events-none absolute inset-[4%] hidden h-[92%] w-[92%] sm:block"
                viewBox="0 0 600 800"
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="splashGoldStroke" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#e6c680" />
                        <stop offset="50%" stopColor="#b48e4b" />
                        <stop offset="100%" stopColor="#8a6a2d" />
                    </linearGradient>
                </defs>
                <polygon
                    points="80,40 520,40 560,80 560,720 520,760 80,760 40,720 40,80"
                    fill="none"
                    stroke="url(#splashGoldStroke)"
                    strokeWidth="1.5"
                    opacity="0.7"
                />
                <polygon
                    points="100,60 500,60 540,100 540,700 500,740 100,740 60,700 60,100"
                    fill="none"
                    stroke="url(#splashGoldStroke)"
                    strokeWidth="0.75"
                    opacity="0.4"
                />
            </svg>

            {/* Centered content */}
            <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center">
                <Ornament className="splash-ornament-in mb-6 h-5 w-24 opacity-85 sm:mb-8 sm:h-6 sm:w-28" />

                <h1
                    className="brand-meyar splash-meyar-in select-none"
                    style={{
                        fontSize: "clamp(5rem, 22vw, 12rem)",
                        ...(lang === "en"
                            ? { fontFamily: "'Playfair Display','Cinzel',serif", letterSpacing: "0.06em" }
                            : null),
                    }}
                    aria-label={brand}
                >
                    {brand}
                </h1>

                <Ornament className="splash-ornament-in mt-6 h-5 w-24 rotate-180 opacity-85 sm:mt-8 sm:h-6 sm:w-28" />

                <div className="splash-tag-in mt-8 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-amber-200/90 backdrop-blur sm:mt-10 sm:px-4 sm:py-1.5 sm:text-[11px]">
                    {tag}
                </div>
            </div>

            {/* Loading dots — fixed bottom */}
            <div
                className="absolute inset-x-0 z-10 flex flex-col items-center gap-2"
                style={{ bottom: "max(2.5rem, env(safe-area-inset-bottom))" }}
            >
                <div className="splash-loader flex items-center gap-1.5" aria-label="جاري التحميل">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                </div>
                <div className="font-heading text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-400/80">
                    Loading
                </div>
            </div>

            {/* Bottom gradient for depth */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-black/50 to-transparent" />
        </div>
    );
}

function Ornament({ className = "" }) {
    return (
        <svg className={className} viewBox="0 0 120 20" fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="splashOrnGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#b48e4b" stopOpacity="0" />
                    <stop offset="50%" stopColor="#e6c680" stopOpacity="1" />
                    <stop offset="100%" stopColor="#b48e4b" stopOpacity="0" />
                </linearGradient>
            </defs>
            <line x1="0" y1="10" x2="50" y2="10" stroke="url(#splashOrnGrad)" strokeWidth="1" />
            <line x1="70" y1="10" x2="120" y2="10" stroke="url(#splashOrnGrad)" strokeWidth="1" />
            <g transform="translate(60 10)">
                <polygon points="0,-7 6,0 0,7 -6,0" fill="#e6c680" />
                <polygon points="0,-3 2.5,0 0,3 -2.5,0" fill="#0b1e33" />
            </g>
        </svg>
    );
}
